// GtInterviewPlan — the Interview tab on a role (and on a bench).
//
// The role's interview setting (Off, Invite by hand, Part of applying) and the
// plan Nathan, the AI interviewer, works from. The AI writes a draft from the
// role; a person reads it, edits it and approves it. Approval is recorded (who,
// when, which version) and nobody can be interviewed against an unapproved
// plan. Editing an approved plan starts a new draft; the approved one stays in
// use until the new one is approved.
import { useState, useEffect, useCallback } from 'react';
import { useSourcingFetch } from './GtSourcing';
import { GtSelect } from './GtControls';
import GtInterviewsPanel from './GtInterviewsPanel';

const MODES = [
  { value: 'off', label: 'Off', help: 'No interview for this role.' },
  { value: 'manual', label: 'Invite by hand', help: 'You choose who Nathan interviews, from the pipeline, the Suggested tray or a candidate record.' },
  { value: 'on_apply', label: 'Part of applying', help: 'Everyone who applies is offered the interview straight after applying. Nobody is rejected automatically.' },
];
const WEIGHT = { 3: 'Must-have', 2: 'Important', 1: 'Nice to have' };

function ukDate(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
function lines(t) { return String(t || '').split(/\r?\n/).map(x => x.trim()).filter(Boolean); }
function copy(o) { return JSON.parse(JSON.stringify(o)); }
function blankCompetency(n) {
  return { key: 'new_' + Date.now(), name: 'New competency ' + n, tests: '', maps_to: '', opening_question: '', depth_markers: [], follow_ups: [], weight: 2, scale: { 1: '', 2: '', 3: '', 4: '', 5: '' } };
}

// ── The setting ────────────────────────────────────────────────────────────
function ModeCard({ mode, waiting, hasApproved, busy, onChange }) {
  return (
    <section className="gtiv-card gtiv-mode">
      <div className="gtiv-mode-h">
        <span className="gtiv-orb" aria-hidden="true" />
        <div>
          <h3>Interview with Nathan</h3>
          <p>Nathan is GTCO's AI interviewer: a live video call of about 25 minutes, transcribed, scored against the plan below and reviewed by a person.</p>
        </div>
      </div>
      <div className="gtiv-modes" role="radiogroup" aria-label="Interview setting">
        {MODES.map(m => (
          <button key={m.value} type="button" role="radio" aria-checked={mode === m.value} disabled={busy}
            className={'gtiv-modeopt' + (mode === m.value ? ' gtiv-modeopt--on' : '')} onClick={() => mode !== m.value && onChange(m.value)}>
            <b>{m.label}</b><span>{m.help}</span>
          </button>
        ))}
      </div>
      {waiting && <p className="gtiv-note gtiv-note--amber">Part of applying starts once the plan below is approved. Until then nobody is offered the interview.</p>}
      {mode === 'manual' && !hasApproved && <p className="gtiv-note">Approve the plan below before inviting anyone.</p>}
    </section>
  );
}

// ── One competency, read or edit ───────────────────────────────────────────
function CompetencyView({ c, n }) {
  const [scaleOpen, setScaleOpen] = useState(false);
  return (
    <article className="gtiv-card gtiv-comp">
      <div className="gtiv-comp-h">
        <span className="gtiv-num">{n}</span>
        <div className="gtiv-comp-t">
          <h4>{c.name}</h4>
          {c.tests && <p>{c.tests}</p>}
        </div>
        <span className={'gtiv-weight gtiv-weight--' + c.weight}>{WEIGHT[c.weight] || 'Important'}</span>
      </div>
      <blockquote className="gtiv-q"><span>Nathan asks</span>{c.opening_question || <em>No opening question yet</em>}</blockquote>
      <div className="gtiv-two">
        <div>
          <span className="gtiv-k">A strong answer shows</span>
          <ul>{(c.depth_markers || []).map((d, i) => <li key={i}>{d}</li>)}</ul>
        </div>
        <div>
          <span className="gtiv-k">Follow-ups</span>
          <ul>{(c.follow_ups || []).map((d, i) => <li key={i}>{d}</li>)}</ul>
        </div>
      </div>
      <div className="gtiv-comp-f">
        {c.maps_to && <span className="gtiv-maps">Covers: {c.maps_to}</span>}
        <button type="button" className="gtiv-link" onClick={() => setScaleOpen(o => !o)} aria-expanded={scaleOpen}>{scaleOpen ? 'Hide scoring scale' : 'Scoring scale'}</button>
      </div>
      {scaleOpen && (
        <ol className="gtiv-scale">
          {[1, 2, 3, 4, 5].map(k => <li key={k}><b>{k}</b><span>{(c.scale || {})[k] || 'Not described'}</span></li>)}
        </ol>
      )}
    </article>
  );
}

function CompetencyEdit({ c, n, total, onChange, onMove, onRemove }) {
  const set = (k, v) => onChange({ ...c, [k]: v });
  const id = 'gtiv-' + c.key;
  return (
    <article className="gtiv-card gtiv-comp gtiv-comp--edit">
      <div className="gtiv-comp-h">
        <span className="gtiv-num">{n}</span>
        <input id={id + '-name'} className="gtiv-in gtiv-in--title" value={c.name} maxLength={80} onChange={e => set('name', e.target.value)} aria-label="Competency name" />
        <div className="gtiv-in--w"><GtSelect value={String(c.weight)} onChange={v => set('weight', Number(v))}
          options={[{ value: '3', label: 'Must-have' }, { value: '2', label: 'Important' }, { value: '1', label: 'Nice to have' }]} /></div>
        <span className="gtiv-order">
          <button type="button" className="gtiv-icon" disabled={n === 1} onClick={() => onMove(-1)} aria-label="Move up">↑</button>
          <button type="button" className="gtiv-icon" disabled={n === total} onClick={() => onMove(1)} aria-label="Move down">↓</button>
          <button type="button" className="gtiv-icon gtiv-icon--rm" onClick={onRemove} aria-label="Remove competency">×</button>
        </span>
      </div>
      <label className="gtiv-f"><span>What it tests</span><input id={id + '-t'} className="gtiv-in" value={c.tests} maxLength={300} onChange={e => set('tests', e.target.value)} /></label>
      <label className="gtiv-f"><span>Opening question (what Nathan says)</span><textarea id={id + '-q'} className="gtiv-in" rows={2} maxLength={400} value={c.opening_question} onChange={e => set('opening_question', e.target.value)} /></label>
      <div className="gtiv-two">
        <label className="gtiv-f"><span>A strong answer shows (one per line)</span><textarea id={id + '-d'} className="gtiv-in" rows={4} value={(c.depth_markers || []).join('\n')} onChange={e => set('depth_markers', lines(e.target.value))} /></label>
        <label className="gtiv-f"><span>Follow-ups (one per line)</span><textarea id={id + '-f'} className="gtiv-in" rows={4} value={(c.follow_ups || []).join('\n')} onChange={e => set('follow_ups', lines(e.target.value))} /></label>
      </div>
      <label className="gtiv-f"><span>Covers (which must-have)</span><input id={id + '-m'} className="gtiv-in" value={c.maps_to} maxLength={300} onChange={e => set('maps_to', e.target.value)} /></label>
      <div className="gtiv-f"><span>Scoring scale</span>
        <ol className="gtiv-scale gtiv-scale--edit">
          {[1, 2, 3, 4, 5].map(k => (
            <li key={k}><b>{k}</b><input id={id + '-s' + k} className="gtiv-in" value={(c.scale || {})[k] || ''} maxLength={300}
              onChange={e => set('scale', { ...(c.scale || {}), [k]: e.target.value })} aria-label={'Scale level ' + k} /></li>
          ))}
        </ol>
      </div>
    </article>
  );
}

// ── The panel ──────────────────────────────────────────────────────────────
export default function GtInterviewPlan({ kind, ownerId, getToken, flash: flashProp, onChanged }) {
  const authFetch = useSourcingFetch(getToken);
  const base = '/api/interview-plans/' + kind + '/' + ownerId;
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [draft, setDraft] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [problems, setProblems] = useState([]);
  const flash = useCallback((m) => { if (flashProp) flashProp(m); }, [flashProp]);

  const load = useCallback(async () => {
    try { setInfo(await authFetch(base)); setError(''); } catch (e) { setError(e.message); }
  }, [authFetch, base]);
  useEffect(() => { load(); }, [load]);

  async function run(what, fn, done) {
    setBusy(what); setProblems([]);
    try {
      const r = await fn();
      setInfo(r);
      if (done) flash(done);
      if (onChanged) onChanged(r);
      return r;
    } catch (e) {
      if (e.body && Array.isArray(e.body.problems)) setProblems(e.body.problems);
      flash(e.message);
      return null;
    } finally { setBusy(''); }
  }
  const generate = () => run('generate', () => authFetch(base + '/generate', { method: 'POST' }), 'Plan written. Read it, edit anything, then approve it.');
  const setMode = (mode) => run('mode', () => authFetch(base + '/mode', { method: 'POST', body: JSON.stringify({ mode }) }), 'Interview setting saved');
  async function save() {
    const r = await run('save', () => authFetch(base + '/plans/' + info.current.id, { method: 'PUT', body: JSON.stringify({ plan: draft }) }),
      info.current.status === 'approved' ? 'Saved as a new draft. The approved plan stays in use until you approve this one.' : 'Plan saved');
    if (r) setDraft(null);
  }
  async function approve() {
    setConfirm(null);
    await run('approve', () => authFetch(base + '/plans/' + info.current.id + '/approve', { method: 'POST' }), 'Plan approved. Nathan will use it for every interview from now on.');
  }

  if (error && !info) return <div className="gtiv-card gtiv-err">Could not load the interview plan: {error} <button type="button" className="gtiv-link" onClick={load}>Try again</button></div>;
  if (!info) return <div className="gtiv-card gtiv-loading">Loading the interview plan…</div>;

  const cur = info.current;
  const plan = draft || (cur && cur.plan);
  const isDraft = cur && cur.status === 'draft';
  const allProblems = problems.length ? problems : info.problems || [];
  const comps = (plan && plan.competencies) || [];
  const newerThanApproved = isDraft && info.approved;

  return (
    <div className="gtiv">
      {kind === 'role' && (
        <ModeCard mode={info.mode} waiting={info.waiting_for_approval} hasApproved={!!info.approved} busy={busy === 'mode'} onChange={setMode} />
      )}

      {cur && (
        <GtInterviewsPanel key={'iv-' + ((info.approved && info.approved.id) || 'none')} kind={kind} ownerId={ownerId} getToken={getToken} flash={flashProp} />
      )}

      {!cur && (
        <section className="gtiv-card gtiv-empty">
          <span className="gtiv-orb gtiv-orb--lg" aria-hidden="true" />
          <h3>{kind === 'role' ? 'Nathan needs a plan for this role' : 'Nathan needs a plan for this bench'}</h3>
          <p>{kind === 'role'
            ? 'The AI writes one from the role: the must-haves, the sourcing brief and the job description. Every must-have becomes something Nathan asks about, with what a strong answer sounds like and how to score it. You then read it, change anything and approve it.'
            : 'A general screen for this bench\'s field, not tied to one role, so people can be vetted before a role arrives. You read it, change anything and approve it.'}</p>
          <button type="button" className="gtc-primary" disabled={busy === 'generate'} onClick={generate}>
            {busy === 'generate' ? 'Writing the plan, about 30 seconds…' : 'Write the plan'}
          </button>
          <span className="gtiv-cost">About {Math.round((info.estimate_gbp || 0.05) * 100)}p</span>
        </section>
      )}

      {cur && (
        <>
          <section className="gtiv-card gtiv-head">
            <div className="gtiv-head-l">
              <span className={'gtiv-pill gtiv-pill--' + (draft ? 'edit' : cur.status)}>
                {draft ? 'Editing' : cur.status === 'approved' ? 'Approved' : cur.status === 'draft' ? 'Draft' : 'Superseded'} · v{cur.version}
              </span>
              <div>
                <b>{info.owner.name}: interview plan</b>
                <span>
                  {comps.length} competencies · about {plan.minutes} minutes · {cur.source === 'edited' ? 'edited by a person' : 'written by AI'}{cur.updated_at ? ', ' + ukDate(cur.updated_at) : ''}
                </span>
                {info.approved && (
                  <span className="gtiv-approved">
                    Approved v{info.approved.version}{info.approved.approved_by_name ? ' by ' + info.approved.approved_by_name : ''} on {ukDate(info.approved.approved_at)}
                    {newerThanApproved ? '. Interviews use v' + info.approved.version + ' until this draft is approved.' : '.'}
                  </span>
                )}
              </div>
            </div>
            <div className="gtiv-acts">
              {draft ? (
                <>
                  <button type="button" className="gtc-btn-ghost" disabled={busy === 'save'} onClick={() => setDraft(null)}>Cancel</button>
                  <button type="button" className="gtc-primary" disabled={busy === 'save'} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save changes'}</button>
                </>
              ) : (
                <>
                  <button type="button" className="gtc-btn-ghost" disabled={!!busy} onClick={() => (isDraft && cur.source === 'edited' ? setConfirm('rewrite') : generate())}>
                    {busy === 'generate' ? 'Rewriting…' : 'Rewrite with AI'}
                  </button>
                  <button type="button" className="gtc-btn-ghost" disabled={!!busy} onClick={() => setDraft(copy(cur.plan))}>Edit</button>
                  {isDraft && (
                    <button type="button" className="gtc-primary" disabled={!!busy || allProblems.length > 0} onClick={() => setConfirm('approve')}
                      title={allProblems.length ? 'Fix the points listed first' : ''}>
                      {busy === 'approve' ? 'Approving…' : 'Approve plan'}
                    </button>
                  )}
                </>
              )}
            </div>
          </section>

          {!draft && allProblems.length > 0 && (
            <section className="gtiv-card gtiv-problems" role="alert">
              <b>Before this can be approved</b>
              <ul>{allProblems.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </section>
          )}

          <section className="gtiv-card gtiv-says">
            {draft ? (
              <>
                <label className="gtiv-f"><span>Nathan opens with</span><textarea id="gtiv-intro" className="gtiv-in" rows={3} maxLength={800} value={draft.intro} onChange={e => setDraft({ ...draft, intro: e.target.value })} /></label>
                <label className="gtiv-f"><span>Nathan closes with</span><textarea id="gtiv-closing" className="gtiv-in" rows={2} maxLength={600} value={draft.closing} onChange={e => setDraft({ ...draft, closing: e.target.value })} /></label>
                <label className="gtiv-f gtiv-f--min"><span>Length in minutes</span><input id="gtiv-min" className="gtiv-in" type="number" min={8} max={45} value={draft.minutes} onChange={e => setDraft({ ...draft, minutes: Number(e.target.value) || 25 })} /></label>
              </>
            ) : (
              <div className="gtiv-two">
                <div><span className="gtiv-k">Nathan opens with</span><p>{plan.intro || 'Not written'}</p></div>
                <div><span className="gtiv-k">Nathan closes with</span><p>{plan.closing || 'Not written'}</p></div>
              </div>
            )}
          </section>

          <div className="gtiv-list">
            {comps.map((c, i) => (draft
              ? <CompetencyEdit key={c.key} c={c} n={i + 1} total={comps.length}
                  onChange={nc => setDraft({ ...draft, competencies: comps.map((x, j) => (j === i ? nc : x)) })}
                  onMove={d => { const a = comps.slice(); const t = a[i]; a[i] = a[i + d]; a[i + d] = t; setDraft({ ...draft, competencies: a }); }}
                  onRemove={() => setDraft({ ...draft, competencies: comps.filter((x, j) => j !== i) })} />
              : <CompetencyView key={c.key} c={c} n={i + 1} />))}
            {draft && (
              <button type="button" className="gtiv-add" onClick={() => setDraft({ ...draft, competencies: comps.concat([blankCompetency(comps.length + 1)]) })}>Add a competency</button>
            )}
          </div>

          {info.versions.length > 1 && (
            <p className="gtiv-versions">
              Versions: {info.versions.map(v => 'v' + v.version + ' ' + (v.status === 'approved' ? 'approved' : v.status === 'superseded' ? 'replaced' : 'draft')).join(' · ')}
            </p>
          )}
        </>
      )}

      {confirm && (
        <div className="gtc-modal-backdrop" onClick={() => setConfirm(null)}>
          <div className="gtc-modal" onClick={e => e.stopPropagation()}>
            <div className="gtc-modal-head">{confirm === 'approve' ? 'Approve version ' + cur.version : 'Rewrite the draft'}</div>
            <div className="gtc-modal-body">
              {confirm === 'approve' ? (
                <>
                  <p className="gtbn-help">Approving records that you have read this plan and are happy for Nathan to use it. It is kept with your name and the date as part of the record of human oversight.</p>
                  <p className="gtbn-help">Every interview from now on uses this version. Interviews already taken keep the version they used.</p>
                </>
              ) : (
                <p className="gtbn-help">The AI will write a fresh draft from the role. The edits in this draft will be replaced. The approved plan, if there is one, is not affected.</p>
              )}
            </div>
            <div className="gtc-modal-foot">
              <button type="button" className="gtc-btn-ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className="gtc-primary" onClick={() => { if (confirm === 'approve') approve(); else { setConfirm(null); generate(); } }}>
                {confirm === 'approve' ? 'Approve plan' : 'Rewrite it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
